import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { orgAdd, orgFetchIds, orgUpdate } from "@/apis/org";
import { Org, OrgAdd, OrgUpdate } from "@/types/org";
import { notification } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { CommonStateEnum } from "@/types/common";

type RmItem = (orgId: string) => Promise<Array<Org>>;
type AddItem = (parentId: string | null, newOrg: OrgAdd) => Promise<Org>;
type UpdateItem =  (orgId: string, updatedOrg: OrgUpdate) => Promise<void>;
export type SwitchOrgsReturnType = [
  Array<Org>,
  {
    addItem: AddItem;
    rmItem: RmItem;
    updateItem: UpdateItem;
  }
];

const useSwitchOrgs = (initialOrgs: Array<Org>): SwitchOrgsReturnType => {
  const { t } = useTranslation();
  const user  = useSelector((state: RootState) => state.userReducer.curUser)
  const [orgs, setOrgs] = useState<Array<Org>>(initialOrgs);

  useEffect(() => {
    initOrgs()
  }, [initialOrgs])

  const initOrgs = () => {
    const orgIds = initialOrgs?.map((org) => org.id);
    if(orgIds && orgIds.length > 0) {
      orgFetchIds(orgIds)
        .then((res) => {
          if(user && user.permissions && user.permissions.length > 0) {
            let orgList:Org[] = []
            res.forEach((ele: Org) => {
                if(checkOrgPermission(ele)){
                  orgList.push(ele)
                }else{
                  orgList.push({...ele, subs: []})
                }
            });
            const rootIdMap = groupByRootId(orgList)
            let newOrgList: Org[] = []
            rootIdMap.forEach((value) => {
              const orgItemData = findRootSub(value)
              if(orgItemData) {
                // 由于value已经根据rootId进行分组，构造数据可能会产生一条数据或者无根节点也就是多条数据, 
                // 若是多条数据可能是构造时数据在进行添加时由于pid虚拟数据还未构造会导致该情况，所以最后需对数据进行处理避免该情况
                if(orgItemData.length > 1) {
                  const formatOrgList = formatTreeData(orgItemData)
                  if(formatOrgList) newOrgList.push(...formatOrgList)
                } else {
                  newOrgList.push(...orgItemData)
                }
              }
            })
            setOrgs(newOrgList)
          } else {
            // 考虑层级参与组织无权限的展示
            const orgs = res?.map((item) => {return {...item, subs: []}})
            const rootIdMap = groupByRootId(orgs)
            const orgTreeList: Org[] = []
            rootIdMap.forEach((value) => {
              const itemData = buildTreeByRootId(value)
              if(itemData && itemData.length > 1) {
                const formatOrgList = formatTreeData(itemData)
                if(formatOrgList) orgTreeList.push(...formatOrgList)
              } else {
                orgTreeList.push(...value)
              }
            })
            setOrgs(orgTreeList)
          }
      }).catch((err) => notification.error({ placement: 'top',message: err}))
    }
  }

  const formatTreeData = (nodes: Org[]): Org[] | null => {
    const findRootNode = (rootId: string, orgs: Org[]): Org | null => {
      for (let i = 0; i < orgs.length; i++) {  
        const org = orgs[i];  
        if (org.id === rootId || org.id.includes(rootId)) {
          return org;
        }  
        if (org.subs && org.subs.length > 0) {  
          const rootNode = findRootNode(rootId, org.subs);  
          if (rootNode) {  
            return rootNode;
          }  
        }
      }
      return null;
    } 
    let toDeleteIndices: number[] = [] 
    nodes.forEach((element, index) => {
      if(element.pId) {
        const itemNode = findRootNode(element.pId, nodes);
        if(itemNode) {
          itemNode.subs?.push(element);
          toDeleteIndices.push(index)
        }
      }
    });
    for (let i = toDeleteIndices.length - 1; i >= 0; i--) {  
      nodes.splice(toDeleteIndices[i], 1);
    } 
    return nodes;
  }
    
  const buildTreeByRootId = (data: Org[]): Org[] | null => {   
    const nodeMap = new Map<string, Org>();  
    let roots: Org[] = []; // 使用数组来存储根节点
    data.forEach(item => {  
        const newNode: Org = { ...item, subs: [] };  
        nodeMap.set(item.id, newNode);
  
        if (!item.pId || item.pId === '') {
          roots.push(newNode);  
        } else {  
            // 尝试找到父节点  
            const parentNode = nodeMap.get(item.pId);  
            if (parentNode) {  
                // 如果父节点存在，则将当前节点添加到父节点的 subs 数组中  
                parentNode.subs?.push(newNode);  
            } else {
                if(!data.some(org => org.id === newNode.pId)) {
                  const xuniNode: Org = {
                    id: `Null_${newNode.pId}`, 
                    name: ' ',
                    subs: [newNode],
                    avatar: '',
                    desc: '',
                    pId: '',
                    rootId: '',
                    state: CommonStateEnum.UN_ACTIVE,
                    path: '',
                    logBookId: '',
                    users: []
                  }
                  const nodePath: string[] = newNode.path.split('#');
                  const indexVal = (newNode.pId && nodePath.indexOf(newNode.pId)) || -1
                  if(indexVal > -1) {
                    insertIntoTreeEnhanced(roots, xuniNode, indexVal);
                  }
                } else {
                  roots.push(item)
                }
            }  
        }  
    });  
    return roots;  
  } 

  const insertIntoTreeEnhanced = (nodes: Org[], newNode: Org, level: number): void => {  
    if (level === 0) {  
      nodes.push(newNode);  
    } else {  
      for (let i = 0; i < nodes.length; i++) {  
        const node = nodes[i];  
        if (!node.subs) {  
          node.subs = []; // 如果子节点不存在，则初始化  
        }  
        if (level === 1) {  
          // 如果当前层级就是目标层级，直接插入  
          node.subs.push(newNode);  
        } else {  
          // 递归到下一层级  
          insertIntoTreeEnhanced(node.subs, newNode, level - 1);  
        }  
      } 
    }  
  }  

  const checkOrgPermission = (org: Org): boolean => {
    return true
  };
  
  const groupByRootId = (items: Org[]): Map<string, Org[]> => {  
    const map = new Map<string, Org[]>();  
    items.forEach(item => {
      if (!map.has(item.rootId)) {  
        map.set(item.rootId, []);  
      }  
      map.get(item.rootId)?.push(item); 
    });  
    return map;    
  } 

  const isExistSub = (orgs: Org[], subId: string): Org | null => {
    const findNode = (orgs: Org[]): Org | null => {
        for (let i = 0; i < orgs.length; i++) {
          const org = orgs[i]
          // 检查当前组织的ID是否匹配pId || 检查当前组织的subs中是否包含目标节点  
          if (org.id === subId || (org.subs && org.subs.some(sub => sub.id === subId))) {  
            return org;
          } 
          // 递归检查subs  
          if (org.subs) {  
            const found = findNode(org.subs);
            if (found) return found;
          }  
        }
      return null
    }
    return findNode(orgs)
  }

  // 找到跟节点
  const findRootSub = (orgArray: Org[]) : Org[] | null => {
    let newOrgList: Org[] | null = null
    let orgPId: string | null = null
    orgArray.forEach((org) => {
      // pid一致时则为同级节点
      if(org.pId === orgPId || (org.subs && org.subs.length === 0)) {
           // 需判断当前节点的父节点是否存在, 数据是否存在父节点中并且考虑多层subs，避免出现数据重复的情况
           let parentNode = (newOrgList || (newOrgList = [])).find(item => item.id === org.pId)
           if (newOrgList && newOrgList.length > 0) parentNode = isExistSub(newOrgList, org.id) || undefined
           if (parentNode) {
            if(parentNode.subs && parentNode.subs.length === 0) {
              parentNode.subs?.push(org)
            }
           } else {
             if(!isExistSub(orgArray, org.id) || org.subs?.length === 0) (newOrgList || (newOrgList = [])).push(org)
           }
      } else {
        orgPId = org.pId;
        const bottomData = findBottomSub(org)
        if(bottomData && org) {
          const path: string[] | null = bottomData.path.includes('#') ? bottomData.path.split('#').filter(item => item !== bottomData.id) : null
          if(path && path.length > 0){
            const orgTreeData = buildOrgTree(path, orgArray)
            if (orgTreeData && !newOrgList?.some(item => item.id === orgTreeData.id)){
              if(orgTreeData) {
                if(isExistSub((newOrgList || (newOrgList = [])), orgTreeData.id)) {
                  newOrgList = [orgTreeData]
                } else {
                  (newOrgList || (newOrgList = [])).push(orgTreeData)
                }
              }
            }
          } else {
            (newOrgList || (newOrgList = [])).push(org)
          }
        }
      }
    })
    return newOrgList
  }

  const findBottomSub = (orgs: Org): Org | null => {  
    let botNode: Org | null = null; 
    const traverse = (nodes: Org) => {  
      if (!nodes.subs || nodes.subs.length === 0) {  
        botNode = {...nodes};  
      } else {
        nodes.subs.forEach(child => {  
          traverse(child);  
        });  
      }  
    }  
    traverse(orgs);  
    return botNode;  
  }

  const buildOrgTree = (path: string[], orgs: Org[]): Org | null => {  
    // 创建一个id:Org的Map
    const orgMap = new Map<string, Org>();  
    orgs.forEach(org => {
      orgMap.set(org.id, { ...org });  
    });
    
    // 在树中找到或创建节点，并返回该节点  
    const findOrCreateNode = (currentPathIndex: number): Org | null => {  
      if (currentPathIndex >= path.length) return null; // 超出path范围  
    
      const currentId = path[currentPathIndex];  
      let currentNode = orgMap.get(currentId);
      
      if (!currentNode) {  
        // 如果当前id不存在于orgs中，则创建一个空的Org对象并添加到映射中  
        currentNode = { 
          id: `Null_${currentId}`, 
          name: ' ',
          subs: [],
          avatar: '',
          desc: '',
          pId: '',
          rootId: '',
          state: CommonStateEnum.UN_ACTIVE,
          path: '',
          logBookId: '',
          users: []
        };
        orgMap.set(currentId, currentNode);
      }  
      
      // 有下级权限停止递归创建
      if(checkOrgPermission(currentNode)) {
        return currentNode
      }
    
      if (currentPathIndex + 1 < path.length) { 
        // 如果不是最后一个元素，则递归创建子节点 
        // const nextId = path[currentPathIndex + 1];
        const nextNode = findOrCreateNode(currentPathIndex + 1);
        if (nextNode && currentNode) {
          // 将创建的子节点添加到当前节点的children中
          if(currentNode?.subs && !currentNode.subs.some(item => item.id === nextNode.id)){
            currentNode.subs.push(nextNode);  
          }
        }  
      }  
    
      return currentNode;  
    };  
    
    // 从path的第一个元素开始构建树  
    const rootId = path[0];  
    let rootOrg = orgMap.get(rootId);
    if (!rootOrg) {  
      // 若path第一个元素数据则根据有数据的节点进行数据构建
      rootOrg = orgMap.get(orgs[0].id)
      findOrCreateNode(path.indexOf(orgs[0].id));
    } else {
      findOrCreateNode(0);
    }
    return rootOrg || null;  
  }

  const addItem: AddItem = (parentId, orgInfo) => {
    return new Promise((resolve, reject) => {
      orgAdd(orgInfo)
        .then((newOrg) => {
          notification.success({ placement: 'top', message: t('layout.addSuccess') });
          if (parentId) {
            setOrgs((prevOrgs) => {
              const addOrgToTree = (orgs: Array<Org>): Array<Org> => {
                return orgs?.map((org) => {
                  if (org.id === parentId) {
                    return {
                      ...org,
                      subs: [...(org.subs || []), newOrg],
                    };
                  } else if (org.subs) {
                    return {
                      ...org,
                      subs: addOrgToTree(org.subs),
                    };
                  }
                  return org;
                });
              };

              return addOrgToTree(prevOrgs);
            });
          } else {
            setOrgs((prevOrgs) => [newOrg, ...prevOrgs]);
          }
          resolve(newOrg);
        })
        .catch((err) => {
          switch (err) {
            default:
              notification.error({ placement: 'top', message: t('layout.addFailed'), description: err });
              break;
          }
          reject(err);
        });
    });
  };

  const rmItem: RmItem = (orgId) => {
    return new Promise((resolve) => {
      // setOrgs((prevOrgs) => {
        const removeOrgFromTree = (orgs: Array<Org>): Array<Org> => {
          return orgs.reduce((acc, org) => {
            if (org.id === orgId) {
              return acc;
            } else {
              return [
                ...acc,
                {
                  ...org,
                  subs: org.subs ? removeOrgFromTree(org.subs) : undefined,
                },
              ];
            }
          }, [] as Array<Org>);
        };

        const newOrgs = removeOrgFromTree(orgs);
        setOrgs(newOrgs);
        resolve(newOrgs)
      // });
      // resolve();
    });
  };

  const updateItem:UpdateItem = (orgId: string, updatedOrg: OrgUpdate) => {
    return new Promise((resolve, reject) => {
      orgUpdate(orgId, updatedOrg)
        .then(() => {
          notification.success({ placement: 'top',message: t('layout.updateSuccess')})
          setOrgs((prevOrgs) => {
            const updateOrgInTree = (orgs: Array<Org>): Array<Org> => {
              return orgs?.map((org) => {
                if (org.id === orgId) {
                  return {
                    ...org,
                    ...updatedOrg,
                    subs: org.subs ? org.subs?.map((sub) => ({ ...sub })) : undefined,
                  };
                } else if (org.subs) {
                  return {
                    ...org,
                    subs: updateOrgInTree(org.subs),
                  };
                }
                return org;
              });
            };
      
            return updateOrgInTree(prevOrgs);
          });
          resolve()
        })
        .catch((error) => {
          notification.error({ placement: 'top',message: t('layout.updateFailed'), description: error})
          reject(error)
        })
    })
  };

  return [
    orgs,
    {
      addItem,
      rmItem,
      updateItem,
    },
  ];
};

export default useSwitchOrgs;
